import { AmountSuperScript } from "components/design/AmountSuperScript";
import { FileIcon } from "components/design/FileIcon";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import RadioGroup from "components/DesignSystem/RadioGroup/RadioGroup";
import Table from "components/DesignSystem/Table/V2/Table";
import * as RECURRING_JOUNAL_ENTRY_UPDATE_TYPES from "constants/recurringJorunalEntryUpdateTypes";
import { Form, Formik } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { MouseEvent, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useGetRelatedLedgerEntryQuery } from "store/apis/generalLedger";
import { useBulkDeleteJournalEntriesMutation } from "store/apis/journalEntry";
import { RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T } from "types/contants/recurringJournalEntryUpdateTypes";
import { InvoiceType, Transactions } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { Preview } from "components/PreviewModal";

export const Invoice = ({ invoice }: { invoice: InvoiceType | null }) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const onPreview = (e: MouseEvent<HTMLButtonElement>, fileId: string) => {
    e.stopPropagation();
    setPreviewFileId(fileId);
  };

  if (invoice) {
    return (
      <div className="t-flex t-justify-center t-items-center">
        <div className="t-relative">
          {invoice?.file_data ? (
            <Button
              customType="ghost_icon"
              onClick={(e) => onPreview(e, invoice?.file_data?.uuid!)}
            >
              <FileIcon fileType={invoice?.file_data?.file_type} size="24" />
            </Button>
          ) : (
            "-"
          )}
        </div>
        <Preview
          showModal={Boolean(previewFileId)}
          closeModal={() => setPreviewFileId(null)}
          groupId={groupId}
          previewId={previewFileId}
        />
      </div>
    );
  }

  return <div className="t-flex t-justify-center t-items-center">-</div>;
};

export const TransactionTable = ({
  transactions,
}: {
  transactions: Transactions[];
}) => {
  const columnHelper = createColumnHelper<Transactions>();

  const columns = [
    columnHelper.accessor("transaction.description", {
      id: "description",
      size: 20,
      header: () => "DESCRIPTION",
      cell: (info) => info.getValue() || "-",
    }),

    columnHelper.accessor("transaction.merchant", {
      id: "merchant",
      size: 20,
      header: () => "VENDOR",
      cell: (info) => info.getValue() || "-",
    }),

    columnHelper.accessor("transaction.category.name", {
      id: "category",
      size: 20,
      header: () => "CATEGORY",
      cell: (info) => info.getValue() || "-",
    }),

    columnHelper.accessor("transaction.amount", {
      id: "debit",
      size: 15,
      header: () => <div className="t-flex t-justify-end">Debit</div>,
      cell: (info) => {
        const amount = info.getValue();

        if (amount > 0) {
          return null;
        }

        if (!Boolean(amount)) {
          return <div className="t-flex t-justify-end">-</div>;
        }

        return (
          <div className="t-flex t-justify-end t-py-2">
            <AmountSuperScript amount={Number(amount)} />
          </div>
        );
      },
    }),

    columnHelper.accessor("transaction.amount", {
      id: "credit",
      size: 15,
      header: () => <div className="t-flex t-justify-end">Credit</div>,
      cell: (info) => {
        const amount = info.getValue();

        if (amount < 0) {
          return null;
        }

        if (!Boolean(amount)) {
          return <div className="t-flex t-justify-end">-</div>;
        }

        return (
          <div className="t-flex t-justify-end t-py-2">
            <AmountSuperScript amount={Number(amount)} />
          </div>
        );
      },
    }),

    columnHelper.accessor("invoices", {
      id: "invoices",
      size: 5,
      header: "invoice",
      cell: (info) => {
        const invoice = info.getValue();

        return <Invoice invoice={invoice[0]} />;
      },
    }),
  ];

  const table = useReactTable({
    data: transactions || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 1,
      maxSize: 100,
    },
  });

  return (
    <Table.Container>
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  style={{ width: `${header.getSize()}%` }}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Table.Row key={row.id}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <Table.Cell
                    key={cell.id}
                    style={{ width: `${cell.column.getSize()}%` }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                );
              })}
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};

export const DeleteJournalEntryModal = ({
  close,
  isOpen,
  ledgerEntryId,
  onDeleteSuccess,
}: ModalProps & {
  ledgerEntryId: string;
  onDeleteSuccess?: (deletedId: string) => void;
}) => {
  const { alertToast, successToast } = useToast();
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const [deleteJournalEntry, { isLoading: isDeleting }] =
    useBulkDeleteJournalEntriesMutation();

  const recurringCheckModal = useModal();

  const { data: relatedLedgerEntry } = useGetRelatedLedgerEntryQuery(
    {
      entityId,
      groupId,
      ledger_entry_id: ledgerEntryId,
    },
    { skip: !entityId || !groupId || !ledgerEntryId || !isOpen }
  );

  const onTransactionDelete = async () => {
    if (relatedLedgerEntry?.ledger_entry.settings?.is_recurring_enabled) {
      return recurringCheckModal.open();
    }

    if (relatedLedgerEntry || ledgerEntryId) {
      try {
        await deleteJournalEntry({
          entityId,
          groupId,
          entryIds: [relatedLedgerEntry?.ledger_entry.uuid || ledgerEntryId!],
          event_update_type: RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT,
        }).unwrap();
        close();
        onDeleteSuccess?.(ledgerEntryId);
        successToast({ message: "Journal entry deleted!" });
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  };

  const onRecurringConfirmation = async ({
    event,
  }: {
    event: RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T;
  }) => {
    if (relatedLedgerEntry || ledgerEntryId) {
      try {
        await deleteJournalEntry({
          entityId,
          groupId,
          entryIds: [relatedLedgerEntry?.ledger_entry.uuid || ledgerEntryId!],
          event_update_type: event,
        }).unwrap();
        close();
        recurringCheckModal.close();
        onDeleteSuccess?.(ledgerEntryId);
        successToast({ message: "Journal entry deleted!" });
      } catch (error) {
        alertToast({
          message: (error as BackendError)?.data?.error?.message,
        });
      }
    }
  };

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>Delete Journal Entries?</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-text-body-lg t-text-text-60">
            This will permanently delete the journal entry. This action cannot
            be undone.
            {relatedLedgerEntry?.ledger_entry.linked_payroll_id && (
              <>
                <div className="t-text-text-30 t-text-body-sm t-italic t-mt-1">
                  This entry has a linked payroll report, which will also be
                  permanently deleted.
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button disabled={isDeleting}>Cancel</Button>
            </Modal.RawClose>
            <Button
              isLoading={isDeleting}
              disabled={isDeleting}
              customType="danger"
              onClick={onTransactionDelete}
            >
              Delete
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      <Modal.Root
        open={recurringCheckModal.isOpen}
        onOpenChange={recurringCheckModal.close}
      >
        <Formik
          onSubmit={onRecurringConfirmation}
          initialValues={{
            event: RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT,
          }}
        >
          {({ submitForm, setFieldValue, values }) => (
            <Modal.Content>
              <Modal.Header>
                <Modal.Title>Are you sure?</Modal.Title>
                <Modal.Close />
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <p className="t-text-body-sm t-text-text-60 t-mb-4">
                    This is a recurring journal entry, this action will delete:
                  </p>
                  <RadioGroup.Root
                    value={values.event}
                    onValueChange={(v) => setFieldValue("event", v)}
                    className="t-flex t-flex-col t-gap-4"
                  >
                    <RadioGroup.Item
                      value={RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT}
                    >
                      <span className="t-text-body-sm">This entry</span>
                    </RadioGroup.Item>
                    <RadioGroup.Item
                      value={
                        RECURRING_JOUNAL_ENTRY_UPDATE_TYPES.UPDATE_CURRENT_AND_FOLLOWING
                      }
                    >
                      <span className="t-text-body-sm">
                        This and following entries
                      </span>
                    </RadioGroup.Item>
                  </RadioGroup.Root>
                  <p className="t-text-text-30 t-text-body-sm t-italic t-m-0 t-mt-3">
                    Note: This won't affect previous entries.
                  </p>
                </Form>
              </Modal.Body>
              <Modal.FooterButtonGroup>
                <Modal.RawClose asChild>
                  <Button>Cancel</Button>
                </Modal.RawClose>
                <Button customType="danger" onClick={submitForm}>
                  Confirm
                </Button>
              </Modal.FooterButtonGroup>
            </Modal.Content>
          )}
        </Formik>
      </Modal.Root>
    </>
  );
};
